import { Constants } from "./../../constants/constants";
import { Component, OnInit } from "@angular/core";
import { environment } from "../../../environments/environment";
import { FeaturesV1 } from "../../../app/constants/features";
import { getPromotionsUrl, managePricesUrl } from "../../utilities/url-utility";
import { ResourceActions } from "../../constants/resource-actions";
import { PermissionsService } from "@app/services/permissions.service";
import { select, Store } from "@ngrx/store";
import { combineLatest, Observable } from "rxjs";
import { KeheNavigationItem } from "@kehe/phoenix-top-navigation-panel";
import { takeUntil } from "rxjs/operators";
import { DestroyableDirective } from "../../abstract/destroyable";
import * as SupplierSelectors from "@app/shared/state/supplier/supplier.selectors";
import * as ShowSelectors from "@app/shared/state/show/show.selectors";
import * as ComponentSelectors from "./cs-navigation.component.selectors";
import { selectShouldHaveAccessToDiscontinuedProducts } from "@app/discontinued-products/discontinued-products.selectors";
import { selectShouldHaveAccessToFI } from "@app/financial-intelligence/financial-intelligence.selectors";

@Component({
  selector: "app-cs-navigation",
  templateUrl: "./cs-navigation.component.html",
  styleUrls: ["./cs-navigation.component.scss"],
  standalone: false,
})
export class CsNavigationComponent
  extends DestroyableDirective
  implements OnInit
{
  public loadingDraftSupplierESNs$: Observable<boolean>;

  items = new Array<KeheNavigationItem>();
  public hasDraftSupplier = false;
  public hasMultipleDraftEsn = false;
  public hasCustomPromotionsAccess = false;
  showManageDraftSuppliersMenu = false;
  showManageActiveSuppliersMenu = false;
  showManageProductsMenu = false;
  showManagePricesMenu = false;
  supplierIsInShow: boolean;
  shouldDisplayDiscontinuedProductsMenu = false;
  vendors = [];
  showFinancialIntelligenceMenu = false;

  showId: string;

  constructor(
    private _permSvc: PermissionsService,
    private _store: Store,
  ) {
    super();
  }

  ngOnInit() {
    combineLatest([
      this._store.select(
        SupplierSelectors.selectDoesUserHaveAccessToSingleDraftEsn,
      ),
      this._store.select(
        SupplierSelectors.selectDoesUserHaveAccessToMultipleDraftEsns,
      ),
      this._store.select(ComponentSelectors.selectHasCustomPromotionsAccess),
    ]).subscribe(
      ([hasOnly1DraftEsn, hasMultipleDraftEsn, hasCustomPromotionsAccess]) => {
        this.hasDraftSupplier = hasOnly1DraftEsn;
        this.hasMultipleDraftEsn = hasMultipleDraftEsn;
        this.hasCustomPromotionsAccess = hasCustomPromotionsAccess;
        this.buildNavMenu();
      },
    );

    this._store
      .select(ShowSelectors.selectFirstEligibleShowId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((showId) => {
        this.showId = showId;
      });

    this._store
      .pipe(
        select(selectShouldHaveAccessToDiscontinuedProducts),
        takeUntil(this.destroy$),
      )
      .subscribe((hasAccess) => {
        this.shouldDisplayDiscontinuedProductsMenu = hasAccess;
      });

    this.buildManagementNavMenuItem();

    this._store
      .select(SupplierSelectors.selectSupplierEsns)
      .subscribe((esns) => {
        this.vendors = esns;
        this.buildManagementNavMenuItem();
      });

    combineLatest([
      this._store.select(
        ComponentSelectors.selectShouldDisplayManageDraftSuppliersMenu,
      ),
    ]).subscribe(([showDraftMenu]) => {
      this.showManageDraftSuppliersMenu = showDraftMenu;
      this.buildManagementNavMenuItem();
    });

    combineLatest([
      this._store.select(
        ComponentSelectors.selectShouldDisplayManageActiveSuppliersMenu,
      ),
    ]).subscribe(([showActiveMenu]) => {
      this.showManageActiveSuppliersMenu = showActiveMenu;
      this.buildManagementNavMenuItem();
    });

    combineLatest([
      this._store.select(
        ComponentSelectors.selectShouldDisplayManageProductsProductsMenu,
      ),
    ]).subscribe(([showProductsMenu]) => {
      [this.showManageProductsMenu, this.showManagePricesMenu] = [
        showProductsMenu,
        showProductsMenu,
      ];
      this.buildNavMenu();
      this.buildManagementNavMenuItem();
    });

    combineLatest([
      this._store.pipe(select(ShowSelectors.selectIsSupplierInShow)),
    ]).subscribe(([supplierIsInShow]) => {
      this.supplierIsInShow = supplierIsInShow;
      this.buildShowNavMenuItem();
    });

    this._store
      .pipe(select(selectShouldHaveAccessToFI), takeUntil(this.destroy$))
      .subscribe((hasAccess) => {
        this.showFinancialIntelligenceMenu = hasAccess;
        this.buildFINavMenuItem();
      });
  }

  private buildNavMenu() {
    this.clearKeheNavigationItems();
    if (!this.hasDraftSupplier) {
      this.buildHomeMenuItem();
      this.buildShowNavMenuItem();
      this.buildDataNavMenuItem();
      this.buildProductsNavMenuItem();
      this.buildPromotionsNavMenuItem();
      this.buildSalesNavMenuItem();
      this.buildManagementNavMenuItem();
      // Re-add FI after the full rebuild so it survives the clear; the splice
      // inside buildFINavMenuItem keeps it positioned before "My Company".
      this.buildFINavMenuItem();
    } else {
      this.buildProductsNavMenuItem();
    }
  }

  private doesUserHaveAction(action: string): boolean {
    return this._permSvc.userHasAction(action);
  }

  private clearKeheNavigationItems() {
    this.items = [];
  }

  private buildHomeMenuItem() {
    const item = new KeheNavigationItem("Home", "/", { exact: true });
    this.items.push(item);
  }

  private buildShowNavMenuItem() {
    const subItems = new Array<KeheNavigationItem>();

    // rebuild nav each time for feature flag changes
    this.removeItemFromNavMenuByDisplayName("Show");

    const ordersListSubItem = new KeheNavigationItem(
      "Manage Show Orders",
      `/show/${this.showId || "no-shows"}/orders`,
      { exact: true },
    );
    subItems.push(ordersListSubItem);

    if (
      this.supplierIsInShow &&
      this.doesUserHaveAction(ResourceActions.SupplierProductV2)
    ) {
      const productsNavItem = new KeheNavigationItem(
        "Manage Products",
        "/show/products",
        { exact: true },
      );
      subItems.push(productsNavItem);
    }

    const item = new KeheNavigationItem(
      "Show",
      "/show",
      { exact: false },
      null,
      subItems,
    );
    this.items.splice(1, 0, item);
  }

  private buildProductsNavMenuItem() {
    const subItems = new Array<KeheNavigationItem>();
    let subItem;

    if (this.showManageProductsMenu) {
      if (this.doesUserHaveAction(ResourceActions.SupplierProductV2)) {
        subItem = new KeheNavigationItem("Manage Products", "/products", {
          exact: false,
        });
        subItems.push(subItem);
      }
    }

    if (this.showManagePricesMenu) {
      const navigationText = "Manage Prices";
      subItems.push(new KeheNavigationItem(navigationText, managePricesUrl));
    }

    if (this.shouldDisplayDiscontinuedProductsMenu) {
      subItem = new KeheNavigationItem(
        "Discontinued Products",
        "/discontinued-products",
      );
      subItems.push(subItem);
    }

    // Don't include the products menu if user is 3rd party of record, irrespetive of subItems length
    if (subItems.length > 0) {
      const item = new KeheNavigationItem(
        "Products",
        "/products",
        { exact: false },
        null,
        subItems,
      );

      const existingIndex = this.items.findIndex(
        (i) => i.display === "Products",
      );
      if (existingIndex > -1) {
        this.items[existingIndex] = item;
      } else {
        this.items.push(item);
      }
    }
  }

  private buildPromotionsNavMenuItem() {
    const subItems = new Array<KeheNavigationItem>();

    const managePromotionalRoadmapsSubItem = new KeheNavigationItem(
      "Manage Promotional Roadmaps",
      "promotional-roadmap",
      { exact: true },
      null,
      null,
      null,
      "manage-promotional-roadmap",
    );
    subItems.push(managePromotionalRoadmapsSubItem);

    if (this.hasCustomPromotionsAccess) {
      const manageCustomPromotionsSubItem = new KeheNavigationItem(
        "Manage Custom Promotions",
        "custom-promotions",
        { exact: true },
        null,
        null,
        null,
        "manage-custom-promotions",
      );
      subItems.push(manageCustomPromotionsSubItem);
    }

    if (this.doesUserHaveAction(FeaturesV1.Promotions)) {
      const subItem = new KeheNavigationItem(
        "Promotions",
        getPromotionsUrl(),
        null,
        null,
        null,
        null,
        "promotions-outer-link",
      );
      subItems.push(subItem);
    }

    const item = new KeheNavigationItem(
      "Promotions",
      "promotional-roadmap",
      { exact: false },
      null,
      subItems,
      null,
      "promotions",
    );

    if (subItems.length > 0) {
      const existingIndex = this.items.findIndex(
        (i) => i.display === "Promotions",
      );

      if (existingIndex > -1) {
        this.items[existingIndex] = item;
      } else {
        this.items.push(item);
      }
    }
  }

  private buildDataNavMenuItem() {
    const dataNavSubItems = new Array<KeheNavigationItem>();

    if (this.doesUserHaveAction(ResourceActions.SupplierBiDataRead)) {
      let connectAnalyticsSubMenuItems: KeheNavigationItem[] = [
        new KeheNavigationItem("Analytics Dashboard", "/analytics"),
        new KeheNavigationItem("Gap & Void Dashboard", "/gap-void"),
      ];

      connectAnalyticsSubMenuItems.push(
        new KeheNavigationItem(
          "Order Projection Dashboard",
          "/order-projection",
        ),
      );

      if (environment.productAvailabilityDashboardId) {
        connectAnalyticsSubMenuItems = [
          ...connectAnalyticsSubMenuItems,
          new KeheNavigationItem(
            "AOM Inventory Gap Dashboard",
            "/product-availability-dashboard",
          ),
        ];
      }

      dataNavSubItems.push(
        new KeheNavigationItem(
          "Connect Analytics",
          "/analytics",
          { exact: true },
          null,
          connectAnalyticsSubMenuItems,
        ),
      );
    }

    const userHasSsrsAccess =
      this.doesUserHaveAction(ResourceActions.SSRSReportsBaseRead) ||
      this.doesUserHaveAction(ResourceActions.SSRSReportsEnhancedRead);
    const userHasDataExchangeAccess = this.doesUserHaveAction(
      ResourceActions.SupplierAnalyticsRead,
    );

    if (userHasSsrsAccess || userHasDataExchangeAccess) {
      const connectDataItems = new Array<KeheNavigationItem>();
      if (userHasSsrsAccess) {
        connectDataItems.push(
          new KeheNavigationItem("Reporting", "/reporting"),
        );
      }
      if (userHasDataExchangeAccess) {
        connectDataItems.push(
          new KeheNavigationItem("Data Exchange", "/data-exchange"),
        );
      }
      dataNavSubItems.push(
        new KeheNavigationItem(
          "Connect Data",
          "/reporting",
          { exact: true },
          null,
          connectDataItems,
        ),
      );
    }

    if (dataNavSubItems.length > 0) {
      this.items.push(
        new KeheNavigationItem(
          `${Constants.KeHE_CONNECT_BI}`,
          "/analytics",
          { exact: false },
          null,
          dataNavSubItems,
        ),
      );
    }
  }

  private buildSalesNavMenuItem() {
    const salesNavSubItems = new Array<KeheNavigationItem>();

    // Removing manage orders tab to not get confused with the other manage show orders page SK-184
    // if (this.doesUserHaveAction(Constants.UserActions.SalesOrderRead)) {
    //   salesNavSubItems.push(new KeheNavigationItem('Manage Orders', '/sales-orders'));
    // }
    if (this.doesUserHaveAction(FeaturesV1.Ksolve)) {
      salesNavSubItems.push(
        new KeheNavigationItem(
          "Credits",
          "/credits",
          { exact: true },
          null,
          new Array<KeheNavigationItem>(
            new KeheNavigationItem(
              "K-Solve",
              `https://${environment.name}connect.kehe.com/ksolve/`,
            ),
          ),
        ),
      );
    }

    if (salesNavSubItems.length > 0) {
      this.items.push(
        new KeheNavigationItem(
          "Sales Orders",
          "/sales",
          { exact: false },
          null,
          salesNavSubItems,
        ),
      );
    }
  }

  private buildManagementNavMenuItem() {
    const subItems = new Array<KeheNavigationItem>();
    const hasDummyESN = this.vendors.includes(Constants.SupplierV1DummyESN);

    let subItem;

    // rebuild nav each time for feature flag changes
    this.removeItemFromNavMenuByDisplayName("My Company");

    if (this.showManageDraftSuppliersMenu) {
      subItems.push(
        new KeheNavigationItem(
          "Manage My Draft Suppliers",
          Constants.ManageDraftSupplierPath,
        ),
      );
    }

    if (this.showManageActiveSuppliersMenu) {
      subItems.push(
        new KeheNavigationItem(
          "Manage Suppliers",
          Constants.ManageSupplierPath,
        ),
      );
    }

    if (!hasDummyESN && !this.hasMultipleDraftEsn) {
      if (this.doesUserHaveAction(FeaturesV1.UserManagement)) {
        subItem = new KeheNavigationItem(
          "Manage Users",
          `https://${environment.name}entitymanagement.kehe.com`,
        );
        subItems.push(subItem);
      }
      if (this.doesUserHaveAction(FeaturesV1.VendorManagement)) {
        subItem = new KeheNavigationItem(
          "Supplier Management",
          `https://${environment.name}connect.kehe.com/vendormanagement/`,
        );
        subItems.push(subItem);
      }
    }

    if (subItems.length > 0) {
      const item = new KeheNavigationItem(
        "My Company",
        "/management",
        { exact: false },
        null,
        subItems,
      );

      // Keep "My Company" immediately after "Connect F.I." when FI is present,
      // regardless of which selector emitted first; otherwise append.
      const fiIndex = this.items.findIndex(
        (i) => i.display === "Connect F.I.®",
      );
      if (fiIndex > -1) {
        this.items.splice(fiIndex + 1, 0, item);
      } else {
        this.items.push(item);
      }
    }
  }

  private buildFINavMenuItem() {
    // Idempotent: drop any existing FI item first so repeated selector
    // emissions don't duplicate it and so it can be repositioned.
    this.removeItemFromNavMenuByDisplayName("Connect F.I.®");

    if (this.showFinancialIntelligenceMenu) {
      const item = new KeheNavigationItem(
        "Connect F.I.®",
        Constants.FinancialIntelligencePath,
      );

      // Always place FI immediately before "My Company" when it exists,
      // otherwise append.
      const myCompanyIndex = this.items.findIndex(
        (i) => i.display === "My Company",
      );
      if (myCompanyIndex > -1) {
        this.items.splice(myCompanyIndex, 0, item);
      } else {
        this.items.push(item);
      }
    }
  }

  private removeItemFromNavMenuByDisplayName(name: string) {
    const index = this.items.findIndex((i) => i.display === name);
    if (index !== -1) {
      this.items.splice(index, 1);
    }
  }
}
